// src/components/GiftCardForm.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import KRGlue from "@lyracom/embedded-form-glue";
import { showToast } from "../../../core/common-service/CommonService";
import { saveAs } from "file-saver";
import "./GiftCardForm.css";
import {
  PAYMENT_GATEWAY_PUBLIC_KEY,
  postCreateGiftCardPaymentForm,
  getOnGiftCardPaymentCallback,
} from "../../../core/api/Api";
import useLangNavigate from "../../common/useLangNavigate";
import { trackCheckoutEvent } from "../../../core/utils/trackCheckoutEvent";
import { PAYMENT_GATEWAY_ENDPOINT } from "../../../core/constants/constants";
import { getCurrencyForLocale, getLocaleFromPath, DEFAULT_LOCALE } from "../../../config/locales";

const GiftCardForm = () => {
  const { t, i18n } = useTranslation();
  const activeLocale = getLocaleFromPath(window.location.pathname) ?? DEFAULT_LOCALE;
  const currency = getCurrencyForLocale(activeLocale);
  const location = useLocation();
  const navigate = useLangNavigate();
  const { email, plan_id } = location.state || {}; // Ensure plan_id is passed from GiftCardPayment
  const [isPaymentFormRendered, setIsPaymentFormRendered] = useState(false);
  const initiationAmountRef = useRef(null);

  const setupPaymentForm = useCallback(async () => {
    const endpoint = PAYMENT_GATEWAY_ENDPOINT;
    const publicKey = PAYMENT_GATEWAY_PUBLIC_KEY;
    try {
      const res = await postCreateGiftCardPaymentForm({ plan_id, email });
      const response = res?.data?.success;
      if (response) {
        const formToken = response?.formToken;
        const orderId = response?.orderId;
        initiationAmountRef.current = response?.final_amount ?? null;
        window.scrollTo({ top: 0, behavior: "smooth" });
        const { KR } = await KRGlue.loadLibrary(endpoint, publicKey);
        await KR.setFormConfig({
          formToken,
          "kr-language": i18n.language,
        });
        await KR.renderElements("#myPaymentForm");
        setIsPaymentFormRendered(true);
        await KR.onSubmit(async (res) => {
          const orderStatus = res.clientAnswer.orderStatus;
          if (orderStatus === "PAID") {
            const callbackRes = await getOnGiftCardPaymentCallback(
              plan_id,
              orderId,
              email
            );
            const successResponse = callbackRes?.data?.success;
            if (successResponse) {
              showToast({ type: "success", message: successResponse?.message });
              saveAs(successResponse?.url, successResponse?.name);

              // Track successful purchase with full ecommerce data
              trackCheckoutEvent({
                eventType: "gift-card",
                transactionId: orderId,
                value: initiationAmountRef.current ?? successResponse?.price ?? 0,
                tax: successResponse?.tax ?? 0,
                shipping: 0,
                items: [
                  {
                    item_id: plan_id,
                    item_name:
                      successResponse?.planName || `Gift Card - ${plan_id}`,
                    item_category: "Gift Card",
                    price: initiationAmountRef.current ?? successResponse?.price ?? 0,
                    quantity: 1,
                  },
                ],
                output: "success",
              });

              navigate(`/thankyou/purchase?order=${orderId}&plan=${plan_id}`);
            }
          }
          return false; // Prevent redirection on form submit
        });

        await KR.onError(async (res) => {
          try {
            const resp = await getOnGiftCardPaymentCallback(
              plan_id,
              orderId,
              email
            );

            trackCheckoutEvent({
              eventType: "gift-card",
              transactionId: orderId,
              value: initiationAmountRef.current ?? resp?.data?.success?.price ?? 0,
              items: [
                {
                  item_id: plan_id,
                  item_name: `Gift Card - ${plan_id}`,
                  item_category: "Gift Card",
                  price: initiationAmountRef.current ?? resp?.data?.success?.price ?? 0,
                  quantity: 1,
                },
              ],
              output: "fail",
            });
          } catch (error) {
            let price = 0;
            if (error?.response?.data?.price !== undefined) {
              price = Number(error.response.data.price);
            }

            trackCheckoutEvent({
              eventType: "gift-card",
              transactionId: orderId,
              value: initiationAmountRef.current ?? price,
              items: [
                {
                  item_id: plan_id,
                  item_name: `Gift Card - ${plan_id}`,
                  item_category: "Gift Card",
                  price: initiationAmountRef.current ?? price,
                  quantity: 1,
                },
              ],
              output: "fail",
            });
          }
        });
      }
    } catch {
      showToast({ type: "error", message: t("error.unknown_error") });
    }
  }, [email, plan_id, navigate]);

  const onInit = useRef(true);

  useEffect(() => {
    if (!onInit.current) return;
    onInit.current = false;
    if (email && plan_id) {
      setupPaymentForm();
    }
  }, [email, plan_id, setupPaymentForm]);

  return (
    <section className="gift-card-form-section">
      <div className="form">
        <div className="d-flex align-items-center justify-content-center">
          <div id="myPaymentForm">
            <div className="kr-smart-form" kr-card-form-expanded="true"></div>
          </div>
        </div>
        {isPaymentFormRendered && (
          <div className="centered-div">{t("for_more_info_contact_us")}</div>
        )}
      </div>
    </section>
  );
};
export default GiftCardForm;
